<template>
  <div class="passenger__info">
    <h3 class="title--m">{{ getUiString(['claim_details', 'details', 'passengers', 'person_title']) }}{{ fullName }}</h3>
      <div class="row" v-for="row in persondata" :key="row.id">
        <div class="cell" v-for="cell in row.cells" :key="cell.label">
          <div class="label">{{ cell.label }}</div>
          <div class="value" v-if="cell.value">{{ cell.value }}</div>
          <div class="value" v-if="!cell.value && !!cell.label">
            <a v-if="isEditable" class="link" @click.prevent="editPerson">{{ getUiString(['form_fields',
              'not_yet_supplied']) }}</a>
            <span v-else>{{ getUiString(['form_fields', 'not_yet_supplied']) }}</span>
          </div>
        </div>
      </div>
    <div class="event-host" @mouseenter.stop="showCannotEditMessage = true"
      @mouseleave.stop="showCannotEditMessage = false">
      <button v-if="person.CanModify || person.CanComplete" @click.prevent="editPerson" class="link link--edit"
        :disabled="!isEditable">
        <lennoc-icon image="edit" />
        <span>{{ getUiString(['claim_details', 'details', 'passengers', 'edit_button']) }}</span>
      </button>
    </div>
    <lennoc-tool-tip v-if="messageVisible" :error="true">{{ notEditableMessage }}</lennoc-tool-tip>
  </div>
</template>

<script>
import { formatDate } from '@/utils/datetime'
import { mapGetters } from 'vuex'
export default {
  name: 'DisplayPerson',
  props: {
    person: {
      type: Object,
      required: true,
      default () {
        return {}
      }
    }
  },
  data () {
    return {
      showCannotEditMessage: false
    }
  },
  computed: {
    ...mapGetters({
      claimPassengers: 'claim/claimPassengers',
      notEditableMessage: 'claim/isNotEditableMessage',
      isEditable: 'claim/isEditableByMe',
      getUiString: 'uistrings/getUiString',
      getPersonNames: 'claim/getPersonNames',
      getCountryName: 'getCountryName',
      passengerToEdit: 'claim/getPassengerToEdit',
      currentClaim: 'claim/currentClaim'
    }),
    messageVisible () {
      return this.showCannotEditMessage && !this.isEditable
    },
    fullName () {
      const tussen = this.person.MiddleName ? this.person.MiddleName + ' ' : ''
      return `${this.person.FirstName} ${tussen}${this.person.LastName}`
    },
    persondata () {
      const p = this.person
      let rawcells = []
      if (p.IsMinor) {
        rawcells = [
          {
            label: this.getUiString(['form_fields', 'BirthDate']),
            value: this.formatBirthdate(p.BirthDate)
          },
          {
            label: this.getUiString(['form_fields', 'BirthPlace']),
            value: p.BirthPlace
          },
          {
            label: this.getUiString(['form_fields', 'StreetName']),
            value: p.StreetName
          },
          {
            label: this.getUiString(['form_fields', 'HouseNumber']),
            value: p.HouseNumber + ' ' + p.HouseNumberExtension
          },
          {
            label: this.getUiString(['form_fields', 'PostalCode']),
            value: p.PostalCode
          },
          { label: this.getUiString(['form_fields', 'City']), value: p.City },
          {
            label: this.getUiString(['form_fields', 'CountryCode']),
            value: this.getCountryName(p.CountryCode)
          },
          { label: '', value: null },
          {
            label: this.getUiString([
              'form_fields',
              'LegalRepresentativeLink1'
            ]),
            value: this.getPerson(p.LegalRepresentativeLink1)
          },
          {
            label: this.getUiString([
              'form_fields',
              'LegalRepresentativeLink2'
            ]),
            value: this.getPerson(p.LegalRepresentativeLink2)
          }
        ]
      } else if (this.currentClaim.IsPassengerAddressRequired) {
        rawcells = [
          {
            label: this.getUiString(['form_fields', 'BirthDate']),
            value: this.formatBirthdate(p.BirthDate)
          },
          {
            label: this.getUiString(['form_fields', 'BirthPlace']),
            value: p.BirthPlace
          },
          {
            label: this.getUiString(['form_fields', 'StreetName']),
            value: p.StreetName
          },
          {
            label: this.getUiString(['form_fields', 'HouseNumber']),
            value: p.HouseNumber + ' ' + p.HouseNumberExtension
          },
          {
            label: this.getUiString(['form_fields', 'PostalCode']),
            value: p.PostalCode
          },
          { label: this.getUiString(['form_fields', 'City']), value: p.City },
          {
            label: this.getUiString(['form_fields', 'CountryCode']),
            value: this.getCountryName(p.CountryCode)
          },
          {
            label: this.getUiString(['form_fields', 'EMailAddress']),
            value: p.EMailAddress
          }
        ]
      } else {
        rawcells = [
          {
            label: this.getUiString(['form_fields', 'BirthDate']),
            value: this.formatBirthdate(p.BirthDate)
          },
          {
            label: this.getUiString(['form_fields', 'BirthPlace']),
            value: p.BirthPlace
          },
          { label: this.getUiString(['form_fields', 'City']), value: p.City },
          {
            label: this.getUiString(['form_fields', 'EMailAddress']),
            value: p.EMailAddress
          }
        ]
      }
      let filteredCells = [...rawcells]

      const getLabel = (field) => this.getUiString(['form_fields', field]).trim().toLowerCase()
      const legalLabels = new Set([getLabel('LegalRepresentativeLink1'), getLabel('LegalRepresentativeLink2')])
      const birthPlaceLabel = getLabel('BirthPlace')

      filteredCells = filteredCells.filter(cell =>
        !(cell.label.trim().toLowerCase() === birthPlaceLabel && !this.person.IsBirthPlaceRequired)
      )

      const legalLinks = filteredCells.filter(cell => legalLabels.has(cell.label.trim().toLowerCase()))
      filteredCells = filteredCells.filter(cell => !legalLabels.has(cell.label.trim().toLowerCase()))

      let rowId = 0

      const rows = filteredCells.reduce((acc, cell, index) => {
        const rowIndex = Math.floor(index / 2)
        if (!acc[rowIndex]) acc[rowIndex] = { id: `row_${rowId++}`, cells: [] }
        acc[rowIndex].cells.push(cell)
        return acc
      }, [])

      if (legalLinks.length) {
        rows.push({ id: `row_${rowId++}`, cells: legalLinks })
      }

      return rows
    }
  },
  mounted () {
    if (this.person.IsMinor && this.passengerToEdit === this.person.Id) {
      this.$store.dispatch('claim/setPassengerToEdit', null)
      this.editPerson()
    }
  },
  methods: {
    getPerson (representative) {
      if (representative) {
        if (representative.LegalRepresentativeLinkType === 'Other') {
          return this.getUiString([
            'claim_details',
            'details',
            'passengers',
            'lr_not_applicable'
          ])
        }
        const persons = this.getPersonNames
        const person = persons.find(p => p.Id === representative.PersonId)
        if (!person) {
          this.editPerson()
          return ''
        }
        return person.Name
      }
      return ''
    },
    editPerson () {
      this.$emit('edit-person')
    },
    formatBirthdate (birthDate) {
      return birthDate ? formatDate(birthDate, 'date') : ''
    }
  }
}
</script>
